import React from 'react';
import { Checkbox, InputNumber, Select, SelectContent } from '@dextech-ai/dex-design-system';
import { toNumberText } from '@/utils/number-input';
import { renderLabelOptions } from '@/utils/select-items';
import PropTypes from 'prop-types';
import { ARRAY_ITEM_TYPES, FORMATS, THEN_TYPES } from './operators';
import { NULLABLE_LABEL, nullableRequiredWarning } from './branchNullability';

const STRING_LIKE = new Set(['string', 'date', 'date-time']);
const NUMERIC = new Set(['integer', 'number']);

/**
 * Per-field constraint editor — renders only the inputs that make sense for
 * the currently selected `type`. Drives both THEN and ELSE branches via the
 * `branch` prop (used only in test-ids / labels).
 *
 * Errors are shaped as `{ [constraintKey]: string[] }` (per validateRules).
 * Each input gets a deterministic `id` + matching `<label htmlFor>`, and
 * `aria-describedby` points at the inline error span when present.
 */
export default function FieldConstraintForm({
	field,
	branch,
	ruleIndex,
	errors,
	onChange,
}) {
	const fieldErrors = errors || {};
	const handleChange = (key, value) => onChange({ ...field, [key]: value });
	const idBase = `xallof-rule-${ruleIndex}-${branch}-${field.fieldName}`;
	const isString = STRING_LIKE.has(field.type);
	const isNumeric = NUMERIC.has(field.type);
	const isArray = field.type === 'array';
	const isProhibited = !!field.prohibited;
	const isAtLeastOne = !!field.atLeastOne;
	// Prohibition compiles to `not: { anyOf: [{ required: [name] }] }`, which
	// only addresses top-level property names — nested display paths would be
	// ambiguous against literal dotted keys, so the toggle is top-level only.
	const canProhibit = typeof field.fieldName === 'string'
		&& !field.fieldName.includes('.')
		&& !field.fieldName.includes('[]');
	// Membership in the branch `anyOf` group compiles to `{ required: [name] }`
	// arms, which address top-level property names for the same reason.
	const canAtLeastOne = canProhibit;
	// DSV-016: `nullableInBranch` widens `type` inside the branch being edited
	// and nowhere else, so the control has to say which branch that is.
	const branchLabel = branch === 'else' ? 'ELSE' : 'THEN';
	// Required + nullable in the SAME branch is the lift_datahub 1.0.2 defect:
	// `required` asserts key presence only, so the pair silently accepts an
	// explicit null in the branch that was meant to reject it. Warn, never
	// block — stored rules already carry this shape and must keep round-tripping.
	const requiredNullableWarning = nullableRequiredWarning(field);

	// Always-visible footer explaining what the selected membership state
	// means for submissions — hover-only titles proved too discoverable-poor
	// for BD users authoring rules.
	let stateHint;
	if (isProhibited) {
		stateHint = 'Not allowed: this field must not be present when this branch '
			+ 'applies — submissions that include it (even as null) fail validation.';
	} else if (isAtLeastOne) {
		stateHint = 'At least one: a submission must provide at least one of the '
			+ 'fields marked ‘at least one’ in this branch. Value limits (min, '
			+ 'max…) come from the field’s own schema definition.';
	} else if (field.required) {
		stateHint = 'Required: this field must be provided whenever this branch '
			+ 'applies. The constraints above apply on top of the field’s own '
			+ 'schema definition.';
	} else {
		stateHint = 'Optional: the constraints above apply only when the field is '
			+ 'provided in a submission.';
	}

	// `required`, `prohibited` and `atLeastOne` are mutually exclusive —
	// checking one clears the others (new object each time; never mutate the
	// incoming field).
	const handleRequiredChange = (checked) => {
		const { prohibited: _p, atLeastOne: _a, ...rest } = field;
		onChange(checked ? { ...rest, required: true } : { ...rest, required: false });
	};
	const handleProhibitedChange = (checked) => {
		const { prohibited: _p, atLeastOne: _a, ...rest } = field;
		onChange(checked ? { ...rest, prohibited: true, required: false } : rest);
	};
	const handleAtLeastOneChange = (checked) => {
		const { prohibited: _p, atLeastOne: _a, ...rest } = field;
		onChange(checked ? { ...rest, atLeastOne: true, required: false } : rest);
	};

	const renderError = (key) => {
		const msgs = fieldErrors[key];
		if (!msgs || msgs.length === 0) return null;
		return (
			<span className="dsv-xallof-builder__error" id={`${idBase}-${key}-err`}>
				{msgs.join(' ')}
			</span>
		);
	};

	const describedBy = (key) => (fieldErrors[key] ? `${idBase}-${key}-err` : undefined);

	return (
		<div className="dsv-xallof-builder__field-constraints">
			<div className="dsv-xallof-builder__field-head">
				<span className="dsv-xallof-builder__field-name">{field.fieldName}</span>
				<div className="dsv-xallof-builder__field-controls">
					<Checkbox
						className="dsv-xallof-builder__required-toggle"
						title="Adds this field to the branch required list"
						checked={!!field.required}
						onCheckedChange={handleRequiredChange}
						data-testid={`${idBase}-required`}
					>
						required
					</Checkbox>
					{canProhibit && (
						<Checkbox
							className="dsv-xallof-builder__required-toggle"
							title="Rejects the submission when this field is present in this branch"
							checked={isProhibited}
							onCheckedChange={handleProhibitedChange}
							data-testid={`${idBase}-prohibited`}
						>
							not allowed
						</Checkbox>
					)}
					{canAtLeastOne && (
						<Checkbox
							className="dsv-xallof-builder__required-toggle"
							title="At least one of the fields marked 'at least one' in this branch must be provided"
							checked={isAtLeastOne}
							onCheckedChange={handleAtLeastOneChange}
							data-testid={`${idBase}-at-least-one`}
						>
							at least one
						</Checkbox>
					)}
					{renderError('prohibited')}
					{!isProhibited && (
						<>
							<label className="dsv-xallof-builder__inline-label" htmlFor={`${idBase}-type`}>
								type
							</label>
							<div className="dsv-xallof-builder__type-select">
								<Select
									id={`${idBase}-type`}
									size="md"
									value={field.type || 'string'}
									onChange={(next) => handleChange('type', next)}
									data-testid={`${idBase}-type`}
								>
									<SelectContent>{renderLabelOptions(THEN_TYPES)}</SelectContent>
								</Select>
							</div>
						</>
					)}
				</div>
			</div>

			{!isProhibited && !isAtLeastOne && (
			<div className="dsv-xallof-builder__constraint-grid">
				{isArray && (
					<>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-array-item-type`}>array item type</label>
							<Select
								id={`${idBase}-array-item-type`}
								size="md"
								value={field.arrayItemType || null}
								onChange={(next) => handleChange('arrayItemType', next || undefined)}
								placeholder="(select)"
								aria-describedby={describedBy('arrayItemType')}
								data-testid={`${idBase}-array-item-type`}
							>
								<SelectContent>{renderLabelOptions(ARRAY_ITEM_TYPES)}</SelectContent>
							</Select>
							{renderError('arrayItemType')}
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-contains-const`}>contains</label>
							<input
								id={`${idBase}-contains-const`}
								className="dsv-modal__input"
								type="text"
								value={field.containsConst ?? ''}
								onChange={(e) => handleChange('containsConst', e.target.value)}
								placeholder="scalar value"
								data-testid={`${idBase}-contains-const`}
							/>
						</div>
					</>
				)}

				{isString && (
					<>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-min-length`}>minLen</label>
							<InputNumber
								id={`${idBase}-min-length`}
								min={0}
								value={field.minLength ?? ''}
								onChange={(v) => handleChange('minLength', toNumberText(v))}
								error={Boolean(describedBy('minLength'))}
								aria-describedby={describedBy('minLength')}
								data-testid={`${idBase}-min-length`}
							/>
							{renderError('minLength')}
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-max-length`}>maxLen</label>
							<InputNumber
								id={`${idBase}-max-length`}
								min={0}
								value={field.maxLength ?? ''}
								onChange={(v) => handleChange('maxLength', toNumberText(v))}
								data-testid={`${idBase}-max-length`}
							/>
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-pattern`}>pattern</label>
							<input
								id={`${idBase}-pattern`}
								className="dsv-modal__input"
								type="text"
								value={field.pattern ?? ''}
								onChange={(e) => handleChange('pattern', e.target.value)}
								placeholder="regex"
								aria-describedby={describedBy('pattern')}
								data-testid={`${idBase}-pattern`}
							/>
							{renderError('pattern')}
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-format`}>format</label>
							<Select
								id={`${idBase}-format`}
								size="md"
								value={field.format ?? ''}
								onChange={(next) => handleChange('format', next || undefined)}
								data-testid={`${idBase}-format`}
							>
								<SelectContent>{renderLabelOptions(FORMATS)}</SelectContent>
							</Select>
						</div>
					</>
				)}

				{isNumeric && (
					<>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-minimum`}>min</label>
							<InputNumber
								id={`${idBase}-minimum`}
								value={field.minimum ?? ''}
								onChange={(v) => handleChange('minimum', toNumberText(v))}
								error={Boolean(describedBy('minimum'))}
								aria-describedby={describedBy('minimum')}
								data-testid={`${idBase}-minimum`}
							/>
							{renderError('minimum')}
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-maximum`}>max</label>
							<InputNumber
								id={`${idBase}-maximum`}
								value={field.maximum ?? ''}
								onChange={(v) => handleChange('maximum', toNumberText(v))}
								data-testid={`${idBase}-maximum`}
							/>
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-exclusive-minimum`}>excl. min</label>
							<InputNumber
								id={`${idBase}-exclusive-minimum`}
								value={field.exclusiveMinimum ?? ''}
								onChange={(v) => handleChange('exclusiveMinimum', toNumberText(v))}
								error={Boolean(describedBy('exclusiveMinimum'))}
								aria-describedby={describedBy('exclusiveMinimum')}
								data-testid={`${idBase}-exclusive-minimum`}
							/>
							{renderError('exclusiveMinimum')}
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-exclusive-maximum`}>excl. max</label>
							<InputNumber
								id={`${idBase}-exclusive-maximum`}
								value={field.exclusiveMaximum ?? ''}
								onChange={(v) => handleChange('exclusiveMaximum', toNumberText(v))}
								data-testid={`${idBase}-exclusive-maximum`}
							/>
						</div>
					</>
				)}

				<div className="dsv-xallof-builder__field-cell">
					<label htmlFor={`${idBase}-enum`}>enum</label>
					<input
						id={`${idBase}-enum`}
						className="dsv-modal__input"
						type="text"
						value={Array.isArray(field.enumValues)
							? field.enumValues.join(', ')
							: (field.enumValues ?? '')}
						onChange={(e) => handleChange('enumValues', e.target.value)}
						placeholder="a, b, c"
						data-testid={`${idBase}-enum`}
					/>
				</div>
				<div className="dsv-xallof-builder__field-cell dsv-xallof-builder__field-cell--checkbox">
					<Checkbox
						className="dsv-xallof-builder__inline-label"
						checked={!!field.nullableInBranch}
						onCheckedChange={(checked) => handleChange('nullableInBranch', checked)}
						aria-describedby={requiredNullableWarning
							? `${idBase}-nullable-scope ${idBase}-nullable-warning`
							: `${idBase}-nullable-scope`}
						data-testid={`${idBase}-nullable`}
					>
						{NULLABLE_LABEL}
					</Checkbox>
					{/* DSV-016: name the branch this widening lands in. The flag is
					    branch-scoped (it widens `type` inside THIS branch only), and
					    authors who could not see that ticked it on the mandatory
					    THEN side while meaning "null when not applicable". */}
					<span
						className="dsv-xallof-builder__hint"
						id={`${idBase}-nullable-scope`}
						data-testid={`${idBase}-nullable-scope`}
					>
						widens the type in {branchLabel} only
					</span>
					{requiredNullableWarning && (
						<span
							className="dsv-xallof-builder__warning"
							role="status"
							id={`${idBase}-nullable-warning`}
							data-testid={`${idBase}-nullable-warning`}
						>
							{requiredNullableWarning}
						</span>
					)}
				</div>
			</div>
			)}

			<p
				className="dsv-xallof-builder__hint dsv-xallof-builder__hint--footer"
				data-testid={`${idBase}-state-hint`}
			>
				{stateHint}
			</p>
		</div>
	);
}

FieldConstraintForm.propTypes = {
	field: PropTypes.object.isRequired,
	branch: PropTypes.oneOf(['then', 'else']).isRequired,
	ruleIndex: PropTypes.number.isRequired,
	errors: PropTypes.object,
	onChange: PropTypes.func.isRequired,
};
